import {Component, DestroyRef, EventEmitter, HostListener, Inject, Output, ViewChild} from '@angular/core';
import {AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {ContentService} from '../../_services/content.service';
import {CommunitiesService} from '../../_services/communities.service';
import {Category, Community, SubGroup, UserAccessLevel} from '../../_types/communities';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {combineLatest, distinctUntilChanged, filter, map, Observable, startWith, take} from 'rxjs';
import {removeNullUndefinedFields} from '../../_utils/remove-null-fields';
import {FeedPost, NewPost, Post, PostAttachment, PostAttachmentType, UpdatePost} from '../../_types/content';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {LocalStorageService} from '../../_services/local-storage.service';
import {Heatmap, Poll} from '../../_types/polls';
import {PollsService} from '../../_services/polls.service';
import {SnackbarService} from '../../_services/snackbar.service';
import {AuthService} from "../../_services/auth.service";
import {localDateShift, mergeDateAndTime, parseISODate} from '../../_utils/date';
import {ActivatedRoute, Router} from "@angular/router";
import {MatAutocompleteSelectedEvent} from "@angular/material/autocomplete";
import {AttachmentsComponent} from '../attachments/attachments.component';
import {
    AutoLink,
    Bold,
    ClassicEditor,
    Essentials,
    Font,
    Heading,
    HorizontalLine,
    Indent,
    Italic,
    Link,
    List,
    Paragraph,
    Subscript,
    Superscript
} from 'ckeditor5';
import {TitleCasePipe} from "@angular/common";
import {ConfirmationDialogComponent} from "../confirmation-dialog/confirmation-dialog.component";
import {ConfirmationDialogData} from "../../_types/shared";

@Component({
    selector: 'app-create-post',
    templateUrl: './create-post.component.html',
    styleUrl: './create-post.component.scss'
})
export class CreatePostComponent {
    @ViewChild('attachmentComponentRef', {static: false}) attachmentComponent?: AttachmentsComponent;
    @Output() removedPollOrHeatmap: EventEmitter<'poll' | 'heatmap'> = new EventEmitter<'poll' | 'heatmap'>()
    @Output() updatedPoll: EventEmitter<void> = new EventEmitter<void>();
    attachmentsValid: boolean = true;
    pollSurveyValid: boolean = true;
    docValid: boolean = true;
    urlValid: boolean = true;
    imageValid: boolean = true;
    audioValid: boolean = true;
    createPostForm: FormGroup;
    lockPostControl: FormControl = this.formBuilder.control(false);
    dateControl: FormControl = this.formBuilder.control({value: new Date(), disabled: true});
    timeControl: FormControl = this.formBuilder.control({value: '00:00', disabled: true});
    revealDateControl: FormControl = this.formBuilder.control({value: null, disabled: false});
    revealTimeControl: FormControl = this.formBuilder.control({value: '00:00', disabled: false});
    attachment: any;
    document: any;
    url: any;
    image: any;
    audio: any;
    survey: any;
    poll: any;

    attachmentType: string = 'text';
    attachmentInitial: boolean = false;
    communities: Community[] = [];
    subgroups: SubGroup[] = [];
    categories: Category[] = [];
    isLoaded: boolean = false;
    editPost?: Post;
    addPoll: boolean = false;
    sending: boolean = false;
    emojiEnabled: boolean = false;
    editPoll?: Poll;
    defaultAttachments: string[] = ['text', 'video', 'videoLink', 'externalLink', 'screenRecording', 'gif', 'heatmap']
    editPollId?: number;
    community: Community | null = null
    editorInstance: any;

    currentSubgroups: SubGroup[] = [];
    filteredSubgroups: Observable<string[]> | any;
    subgroupSearchControl: FormControl = new FormControl();
    public Editor: typeof ClassicEditor = ClassicEditor;
    public config = {
        plugins: [Link, AutoLink, Essentials, Bold, Font, Italic, Paragraph, List, Heading, Indent, Subscript, Superscript, HorizontalLine],
        toolbar: [
            'undo', 'redo', '|', 'link', '|',
            'bold', 'italic', 'heading', 'strikethrough', 'subscript', 'superscript', '|',
            'horizontalLine', '|',
            'fontSize', 'fontWeight', '|',
            'numberedList', 'bulletedList', 'outdent', 'indent'
        ], link: {
            // Automatically add target="_blank" and rel="noopener noreferrer" to all external links.
            addTargetToExternalLinks: true,
        },
        licenseKey: 'GPL'
    }

    filteredPosts: FeedPost[] = [];
    postSearchControl: FormControl = new FormControl();
    postSearchLoading: boolean = false;


    docInUse: boolean = false;
    audioInUse: boolean = false;
    urlInUse: boolean = false;
    imageInUse: boolean = false;
    surveyPollInUse: boolean = false;
    multiAttachmentInUse: boolean = false;

    constructor(
        private readonly matDialog: MatDialogRef<CreatePostComponent>,
        private readonly dialog: MatDialog,
        private readonly formBuilder: FormBuilder,
        private readonly contentService: ContentService,
        private readonly communitiesService: CommunitiesService,
        private readonly localStorageService: LocalStorageService,
        private readonly pollsService: PollsService,
        private readonly destroyRef: DestroyRef,
        private readonly authService: AuthService,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly snackbarService: SnackbarService,
        @Inject(MAT_DIALOG_DATA) private data: { post: Post, poll?: Poll }
    ) {
        this.contentService.getCurrentDashboardCommunity()
            .pipe(takeUntilDestroyed())
            .subscribe(community => {
                this.community = community;
            });
        this.createPostForm = this.formBuilder.group({
            title: this.formBuilder.control('', Validators.required),
            text: this.formBuilder.control('', Validators.required),
            community: this.formBuilder.control(null, Validators.required),
            subgroup: this.formBuilder.control(null),
            categories: this.formBuilder.control({value: null, disabled: true}),
            is_pinned: this.formBuilder.control(false),
            is_pinned_to_sidebar: this.formBuilder.control(false),
            hide_unmoderated_comments: this.formBuilder.control(false),
            is_closed_loop: this.formBuilder.control(false),
            show_attachment_above_text: this.formBuilder.control(false),
            is_anonymous: this.formBuilder.control(false),
            original_post: this.formBuilder.control(null),
            lock_post_at: this.formBuilder.control({value: null, disabled: true}),
            off_comments_at_time: this.formBuilder.control({value: false, disabled: true}),
            off_likes_at_time: this.formBuilder.control({value: false, disabled: true}),
            off_dislikes_at_time: this.formBuilder.control({value: false, disabled: true}),
            is_hidden: this.formBuilder.control(false),
        });

        this.createPostForm.get('community')?.valueChanges.pipe(
            distinctUntilChanged(),
            takeUntilDestroyed()
        ).subscribe((id) => {
            this.changeCommunity(id);
            this.loadSubgroups(id);
            this.getCategories(id);
        });
        if (this.isSuperuser) {
            this.communitiesService.getAllCommunities().pipe(takeUntilDestroyed()).subscribe(data => {
                this.communities = data;
                if (this.lastSelectedCommunity) {
                    this.emojiEnabled = this.lastSelectedCommunity?.emoji_enabled;
                    this.createPostForm.get('community')?.setValue(this.lastSelectedCommunity.id);
                    this.createPostForm.get('community')?.updateValueAndValidity();
                } else if (this.communities.length === 1) {
                    this.createPostForm.get('community')?.setValue(this.communities[0].id);
                    this.createPostForm.get('community')?.updateValueAndValidity();
                    this.changeCommunity(this.communities[0].id);
                }
                this.isLoaded = true;
                if (this.data?.post) {
                    this.editPost = this.data?.post;
                    this.editPoll = this.data?.poll;
                    this.editPollId = this.data?.poll?.id;
                    this.addPoll = !!this.data?.poll;

                    this.loadDataForEdit(this.editPost)
                }
            });

        } else {
            this.communitiesService.getUserCommunities().pipe(takeUntilDestroyed()).subscribe(data => {
                this.communities = data.map(result => result.community);
                if (this.lastSelectedCommunity) {
                    this.emojiEnabled = this.lastSelectedCommunity?.emoji_enabled;
                    this.createPostForm.get('community')?.setValue(this.lastSelectedCommunity.id);
                    this.createPostForm.get('community')?.updateValueAndValidity();
                } else if (this.communities.length === 1) {
                    this.createPostForm.get('community')?.setValue(this.communities[0].id);
                    this.createPostForm.get('community')?.updateValueAndValidity();
                    this.changeCommunity(this.communities[0].id);
                }
                this.isLoaded = true;
                if (this.data?.post) {
                    this.editPost = this.data?.post;
                    this.editPoll = this.data?.poll;
                    this.editPollId = this.data?.poll?.id;
                    this.addPoll = !!this.data?.poll;

                    this.loadDataForEdit(this.editPost)
                }
            });
        }
        this.manageLockState();

        this.postSearchControl.valueChanges.subscribe(value => {
            this.postSearchLoading = true;
            let filterValue: string = '';
            if (typeof value == 'string') {
                filterValue = value.toLowerCase();
            }
            const filters = {
                search: filterValue,
                is_closed_loop: false,
            }
            this.contentService.getFeed(this.lastSelectedCommunity?.id!, 1, 1000000, filters).pipe(take(1))
                .subscribe({
                    next: result => {
                        this.filteredPosts = result.results;
                        this.postSearchLoading = false;
                    }, error: err => {
                        this.filteredPosts = [];
                        this.postSearchLoading = false;
                    }
                });
        })

        this.filteredSubgroups = this.subgroupSearchControl.valueChanges
            .pipe(
                startWith(''),
                map(value => this._filterSubgroups(value))
            );
    }

    @HostListener('window:beforeunload', ['$event'])
    beforeUnloadHandler(event: Event): void {
        event.preventDefault();
        event.returnValue = true;
    }

    get isClosedLoop(): boolean {
        return this.createPostForm.get('is_closed_loop')?.value || false
    }

    get communityId(): number | null {
        return this.createPostForm.get('community')?.getRawValue();
    }

    get lastSelectedCommunity(): Community | null {
        return this.localStorageService.getItem<Community | null>('lastSelectedCommunity', null);
    }

    get textFormControl(): FormControl {
        return this.createPostForm?.get('text') as FormControl;
    }

    get initialDocument(): PostAttachment | null {
        let document: PostAttachment | null = null
        if (this.editPost?.attachments) {
            for (let attachment of this.editPost?.attachments) {
                if (attachment.attachment_type == 'document') {
                    document = attachment;
                    this.docInUse = true;
                }
            }
        }
        return document;
    }

    get initialUrl(): PostAttachment | null {
        let URL: PostAttachment | null = null
        if (this.editPost?.attachments) {
            for (let attachment of this.editPost?.attachments) {
                if (attachment.attachment_type == 'url') {
                    URL = attachment;
                    this.urlInUse = true;
                }
            }
        }
        return URL;
    }

    get initialAudio(): PostAttachment | null {
        let audio: PostAttachment | null = null
        if (this.editPost?.attachments) {
            for (let attachment of this.editPost?.attachments) {
                if (attachment.attachment_type == 'audio') {
                    audio = attachment;
                    this.audioInUse = true;
                }
            }
        }
        return audio;
    }

    get initialImage(): PostAttachment | null {
        let image: PostAttachment | null = null
        if (this.editPost?.attachments) {
            for (let attachment of this.editPost?.attachments) {
                if (attachment.attachment_type == 'image') {
                    this.imageInUse = true;
                    image = attachment;
                }
            }
        }
        return image;
    }

    get initialSurveyPoll(): PostAttachment | Poll | undefined {
        let survey: PostAttachment | null = null
        if (this.editPost?.attachments) {
            for (let attachment of this.editPost?.attachments) {
                if (attachment.attachment_type == 'survey_url') {
                    this.surveyPollInUse = true;
                    survey = attachment;
                }
            }
        }
        return survey ?? this.editPoll;
    }

    get initialAttachment(): PostAttachment | Heatmap | Poll | undefined {
        let initial: PostAttachment | null = null
        if (this.editPost?.attachments) {
            for (let attachment of this.editPost?.attachments) {
                if (this.defaultAttachments.includes(attachment.attachment_type) || attachment.attachment_type == 'video_url' || attachment.attachment_type == 'gif_url') {
                    initial = attachment;
                }
            }
        }
        if (initial ?? this.editPost?.heatmap_surveys?.[0] ?? this.editPoll) {
            this.multiAttachmentInUse = true;
        }
        return initial ?? this.editPost?.heatmap_surveys?.[0] ?? this.editPoll;
    }

    get isSuperuser(): boolean {
        return this.authService.isSuperUser();
    }

    public onReady(editor: any): void {
        this.editorInstance = editor
        editor.ui.getEditableElement().parentElement.insertBefore(
            editor.ui.view.toolbar.element,
            editor.ui.getEditableElement()
        );
    }

    attachmentsValidEmitter(event: boolean): void {
        this.attachmentsValid = event
    }

    pollSurveyValidEmitter(event: boolean): void {
        this.pollSurveyValid = event
    }

    audioValidEmitter(event: boolean): void {
        this.audioValid = event
    }

    docValidEmitter(event: boolean): void {
        this.docValid = event
    }

    urlValidEmitter(event: boolean): void {
        this.urlValid = event
    }

    imageValidEmitter(event: boolean): void {
        this.imageValid = event
    }

    hasCommunityRole(roles: UserAccessLevel[]): boolean {
        return this.contentService.userHasCommunityRole(roles);
    }

    getAttachmentField(event: any): void {
        this.attachmentType = event.type;
        this.attachment = event.value;
        this.attachmentInitial = event.initial;
        if (this.attachmentType == 'text') {
            this.attachmentsValid = true;
        }
    }

    setSurveyPoll(event: any): void {
        if (event.type == 'survey_url' || event.type == 'survey') {
            this.survey = event.value
            this.poll = null
        } else if (event.type == 'poll' && event.value) {
            this.poll = event.value
            for (let question of this.poll?.questions) {
                // for some reason display_condition_options_identifiers gets turned into an object at some point so turn back into array
                if (question.display_condition_options_identifiers) {
                    question.display_condition_options_identifiers = question.display_condition_options_identifiers.map((condition: any) =>
                        typeof condition === 'object' ? Object.values(condition).join('') : condition
                    );
                }
            }
            this.survey = null
        } else {
            this.poll = null
            this.survey = null
            this.pollSurveyValid = true;
        }
    }

    setDocument(event: any): void {
        if (event.type == 'document') {
            this.document = event.value;
        } else {
            this.document = null;
            this.docValid = true;
        }
    }

    setUrl(event: any): void {
        if (event.type == 'url') {
            this.url = event.value;
        } else {
            this.url = null;
            this.urlValid = true;
        }
    }

    setAudio(event: any): void {
        if (event.type == 'audio') {
            this.audio = event.value;
        } else {
            this.audio = null;
            this.audioValid = true;
        }
    }

    setImage(event: any): void {
        if (event.type == 'image') {
            this.image = event.value;
        } else {
            this.image = null;
            this.imageValid = true;
        }
    }

    createPost(): void {
        this.sending = true
        let postData: NewPost;
        let type: PostAttachmentType | 'text';

        switch (this.attachmentType) {
            case 'videoLink':
                type = 'video_url';
                break;
            case 'gif':
                type = 'gif_url';
                this.attachment = this.attachment.embed_url;
                break;
            default:
                type = this.attachmentType as PostAttachmentType | 'text';
        }

        postData = this.addAttachmentToPayload(type) as NewPost;
        postData = {...postData, emoji_enabled: true};

        // @ts-ignore
        if (postData.categories && postData.categories?.length <= 0 || postData.categories == 0) {
            delete postData['categories'];
        }
        if (this.currentSubgroups?.length > 0) {
            postData['subgroups'] = []
            for (let subgroup of this.currentSubgroups) {
                postData['subgroups']?.push(subgroup.id)
            }
        } else {
            delete postData['subgroups'];
        }
        if (postData.lock_post_at) {
            postData.lock_post_at = new Date(postData.lock_post_at).toISOString();
        }

        if (postData.is_hidden && this.revealDateControl.value && this.revealTimeControl.value) {
            const date: Date = mergeDateAndTime(this.revealDateControl.value, this.revealTimeControl.value);
            postData.reveal_post_at = new Date(date).toISOString();
        }
        if (postData.is_closed_loop) {
            postData['original_post'] = this.postSearchControl?.value?.id;
        } else {
            delete postData['original_post'];
        }


        if (this.survey) {
            postData = {
                ...postData,
                survey_urls: this.survey
            }
        }

        if (this.document) {
            if (postData.attachment_files) {
                postData.attachment_files.push(this.document)
            } else {
                postData = {
                    ...postData,
                    attachment_files: [this.document]
                }
            }
        }
        if (this.url) {
            postData = {
                ...postData,
                urls: this.url
            }
        }
        if (this.image) {
            if (postData.attachment_files) {
                postData.attachment_files.push(this.image)
            } else {
                postData = {
                    ...postData,
                    attachment_files: [this.image]
                }
            }
        }
        if (this.audio) {
            if (postData.attachment_files) {
                postData.attachment_files.push(this.audio)
            } else {
                postData = {
                    ...postData,
                    attachment_files: [this.audio]
                }
            }
        }

        this.contentService.newPost(postData)
            .subscribe((res): void => {
                if (this.poll) {
                    this.poll!.post = res.id;
                    this.pollsService.createNewPoll(removeNullUndefinedFields(this.poll!, true)).subscribe(r => {
                        if (type === 'heatmap') {
                            this.sendHeatMap(res.id)
                        } else {
                            this.postSuccess(res.id);
                        }
                    }, err => {
                        this.sending = false;
                        const error: string = err.includes('0 Unknown Error') ? 'Your file is too large, please try uploading a smaller one' : err;
                        this.snackbarService.openErrorSnackBar(error);
                    })
                } else if (type === 'heatmap') {
                    this.sendHeatMap(res.id)
                } else {
                    this.postSuccess(res.id);
                }
            }, err => {
                this.sending = false;
                const error: string = err.includes('0 Unknown Error') ? 'Your file is too large, please try uploading a smaller one' : err;
                this.snackbarService.openErrorSnackBar(error);
            });
    }

    sendHeatMap(id: number, edit?: boolean): void {
        this.attachment!.post = id;
        if (!edit) {
            this.pollsService.createNewHeatmap(removeNullUndefinedFields(this.attachment!, true)).subscribe(r => {
                this.postSuccess();
            }, err => {
                this.sending = false;
                const error: string = err.includes('0 Unknown Error') ? 'Your file is too large, please try uploading a smaller one' : err;
                this.snackbarService.openErrorSnackBar(error);
            })
        } else {
            this.pollsService.editHeatmap(removeNullUndefinedFields(this.attachment!, true)).subscribe(r => {
                this.postSuccess();
            }, err => {
                this.sending = false;
                const error: string = err.includes('0 Unknown Error') ? 'Your file is too large, please try uploading a smaller one' : err;
                this.snackbarService.openErrorSnackBar(error);
            })
        }
    }

    postSuccess(id?: number): void {
        this.snackbarService.openSuccessSnackBar('Post has been successfully created.');
        this.sending = false;
        this.matDialog.close(true);
        if (id) {
            this.router.navigate(['/feed', id]);
        }
    }

    update(): void {
        if (this.attachmentComponent?.commentType === 'poll' && this.attachmentComponent?.isEditPoll) {
            this.attachmentComponent?.createPollComponent?.updatePoll();
            this.updatedPoll.pipe(take(1)).subscribe(() => this.updatePost());
        } else {
            this.updatePost();
        }
    }

    updatePost(): void {
        this.sending = true;
        let postData: UpdatePost;
        let type: PostAttachmentType | 'text';
        let remove_subgroups = null;
        let unlock_post = null;
        switch (this.attachmentType) {
            case 'videoLink':
                type = 'video_url';
                break;
            case 'gif':
                type = 'gif_url';
                this.attachment = this.attachment.embed_url;
                break;
            default:
                type = this.attachmentType as PostAttachmentType | 'text';
        }
        postData = removeNullUndefinedFields(this.createPostForm.value, true);

        if (postData.is_hidden && this.revealDateControl.value && this.revealTimeControl.value) {
            const date: Date = mergeDateAndTime(this.revealDateControl.value, this.revealTimeControl.value);
            postData.reveal_post_at = new Date(date).toISOString();
        }

        if (!this.attachmentInitial || this.editPost?.attachments?.[0]?.attachment_type !== type) {
            postData = this.addAttachmentToPayload(type) as UpdatePost;

            // TODO instead of deleting and recreating check if unchanged
            if (this.editPost?.attachments) {
                let attachments_to_delete: number[] = [];
                for (let attachment of this.editPost?.attachments) {
                    attachments_to_delete.push(attachment.id)
                }
                if (attachments_to_delete.length > 0) {
                    postData = {
                        ...postData,
                        attachments_to_delete: attachments_to_delete.join(", ")
                    }
                }
            }
        }
        if (this.survey) {
            postData = {
                ...postData,
                survey_urls: this.survey
            }
        }

        if (this.document) {
            if (postData.attachment_files) {
                postData.attachment_files.push(this.document)
            } else {
                postData = {
                    ...postData,
                    attachment_files: [this.document]
                }
            }
        }
        if (this.url) {
            postData = {
                ...postData,
                urls: this.url
            }
        }

        if (this.image) {
            if (postData.attachment_files) {
                postData.attachment_files.push(this.image)
            } else {
                postData = {
                    ...postData,
                    attachment_files: [this.image]
                }
            }
        }
        if (this.audio) {
            if (postData.attachment_files) {
                postData.attachment_files.push(this.audio)
            } else {
                postData = {
                    ...postData,
                    attachment_files: [this.audio]
                }
            }
        }

        if ((postData as any)['polls']) {
            delete (postData as any)['polls'];
        }
        // @ts-ignore
        if (postData.categories && postData.categories?.length <= 0 || postData.categories == 0) {
            postData['categories'] = [];
        }

        if (this.currentSubgroups?.length > 0) {
            postData['subgroups'] = []
            for (let subgroup of this.currentSubgroups) {
                postData['subgroups']?.push(subgroup.id)
            }
        } else {
            delete postData['subgroups']
            remove_subgroups = true;
        }

        if (postData.lock_post_at) {
            postData.lock_post_at = new Date(postData.lock_post_at).toISOString();
        } else {
            unlock_post = true;
        }

        if (postData.is_closed_loop) {
            postData['original_post'] = this.postSearchControl?.value?.id;
        } else {
            delete postData['original_post'];
        }

        postData.moderated = false;

        if (postData.attachment_files) {
            postData.attachment_files = postData.attachment_files.filter(function (el): boolean {
                return el != null;
            });
        }

        if (!postData.attachment_files) {
            delete postData['attachment_files'];
        }


        this.contentService.updatePost(this.editPost!.id, postData, false, {remove_subgroups, unlock_post})
            .subscribe((res): void => {
                if (this.poll) {
                    this.poll!.post = res.id;
                    this.pollsService.createNewPoll(removeNullUndefinedFields(this.poll!, true)).subscribe(r => {
                        if (type === 'heatmap') {
                            this.sendHeatMap(res.id, true)
                        } else {
                            this.postSuccess();
                        }
                    }, err => {
                        this.sending = false;
                        const error: string = err.includes('0 Unknown Error') ? 'Your file is too large, please try uploading a smaller one' : err;
                        this.snackbarService.openErrorSnackBar(error);
                    })
                } else if (type === 'heatmap') {
                    this.sendHeatMap(res.id, true)
                } else {
                    this.postSuccess();
                }
            }, err => {
                this.sending = false;
                const error: string = err.includes('0 Unknown Error') ? 'Your file is too large, please try uploading a smaller one' : err;
                this.snackbarService.openErrorSnackBar(error);
            });

    }

    clearDate(): void {
        this.dateControl?.reset(null);
    }
    clearRevealDate(): void {
        this.revealDateControl?.reset(null);
    }

    selectSubgroup(event: MatAutocompleteSelectedEvent): void {
        const subgroup = event as any;
        this.currentSubgroups.push(subgroup);
        this.subgroupSearchControl.patchValue('')
    }

    removeSubgroup(metric: any): void {
        const i: number = this.currentSubgroups.findIndex((x: any): boolean => x === metric);
        if (i != null && i >= 0) {
            this.currentSubgroups.splice(i, 1);
        }
    }

    displayFn(subgroup: any): string {
        return subgroup && subgroup.name ? subgroup.name : '';
    }

    postDisplayFn(post: FeedPost): string {
        return post && post.title ? post.title : '';
    }

    checkIfInArray(item: any, otherArray: any): boolean {
        let i;
        if (otherArray) {
            i = otherArray.findIndex((x: any): boolean => x === item);
        }
        return (i != null && i >= 0);
    }

    setUse(event: any, value: any): void {
        switch (value) {
            case 'docInUse':
                this.docInUse = event;
                break;
            case 'urlInUse':
                this.urlInUse = event;
                break;
            case 'imageInUse':
                this.imageInUse = event;
                break;
            case 'surveyPollInUse':
                this.surveyPollInUse = event;
                break;
            case 'multiAttachmentInUse':
                this.multiAttachmentInUse = event;
                break;
            case 'audioInUse':
                this.audioInUse = event;
                break;
        }
    }

    validateForm(formGroup: FormGroup | FormArray, parentKey: string = ''): string {
        let errors: string = ''
        const titleCasePipe = new TitleCasePipe();

        Object.keys(formGroup.controls).forEach(key => {
            const control: AbstractControl<any, any> | null = formGroup.get(key);
            const controlPath: string = parentKey ? `${parentKey}.${key}` : key;


            if (control instanceof FormGroup || control instanceof FormArray) {
                this.validateForm(control, controlPath);  // Recursive call
            } else if (control && control.invalid) {
                errors = errors + `Invalid field: ` + titleCasePipe.transform(controlPath) + `, `
            }
        });

        if (!this.docValid) {
            errors = errors + `Document invalid, `
        }
        if (!this.urlValid) {
            errors = errors + `Url invalid, `
        }
        if (!this.pollSurveyValid) {
            errors = errors + `Poll/Survey form invalid, `
        }
        if (!this.attachmentsValid) {
            errors = errors + `Media attachment invalid, `
        }
        return errors
    }

    private addAttachmentToPayload(type: PostAttachmentType | 'text'): NewPost | UpdatePost {
        if (type === 'video_url') {
            return {
                ...removeNullUndefinedFields(this.createPostForm.value, true),
                video_urls: this.attachment
            };
        } else if (type === 'gif_url') {
            return {
                ...removeNullUndefinedFields(this.createPostForm.value, true),
                gif_urls: this.attachment
            };
        } else if (type !== 'text' && type !== 'poll' && type !== 'heatmap') {
            return {
                ...removeNullUndefinedFields(this.createPostForm.value, true),
                attachment_files: [this.attachment]
            };
        } else {
            return removeNullUndefinedFields(this.createPostForm.value, true);
        }
    }

    private loadSubgroups(communityId: number): void {
        this.createPostForm.get('subgroup')?.reset(null);
        if (!communityId) return;
        this.communitiesService.getSubGroups(communityId).pipe(take(1))
            .subscribe(subgroups => {
                this.subgroups = subgroups;
                if (!this.subgroups.length) {
                    this.createPostForm.get('subgroup')?.disable();
                } else {
                    if (!this.data?.post) {
                        // fill current subgroups if edit post has subgroups
                        let currentSubgroup = this.route.snapshot.queryParamMap.get('subgroup')
                        if (currentSubgroup) {
                            for (let subgroup of this.subgroups) {
                                if (currentSubgroup == String(subgroup.id)) {
                                    this.currentSubgroups.push(subgroup)
                                }
                            }
                        }
                    } else if (this.data?.post?.subgroups) {
                        // if user in a subgroup default to subgroup if not editing post
                        for (let subgroup of this.subgroups) {
                            for (let currentsubgroup of this.data?.post?.subgroups) {
                                if (currentsubgroup.id == subgroup.id) {
                                    this.currentSubgroups.push(subgroup)
                                }
                            }
                        }
                    }
                }
            });


    }

    private loadDataForEdit(post: Post): void {
        this.createPostForm.patchValue(post);
        this.lockPostControl.patchValue(!!post.lock_post_at);
        if (post.original_post) {
            this.postSearchControl.setValue(post.original_post);
        }
        if (post.lock_post_at) {
            const {date, time} = parseISODate(post.lock_post_at);
            this.dateControl.patchValue(date);
            this.timeControl.patchValue(time);
            this.lockPostControl.get('lock_post_at')?.patchValue(localDateShift(post.lock_post_at));
        }
        if (post.reveal_post_at) {
            const {date, time} = parseISODate(post.reveal_post_at);
            this.revealDateControl.patchValue(date);
            this.revealTimeControl.patchValue(time);
        }

        if (post.categories) {
            let categoryIds: number[] = []
            for (let category of post.categories) {
                let i = this.categories.findIndex((x: any): boolean => x.id === category.id);
                if (i != null && i >= 0) {
                    // only add category if it hasn't been deleted
                    categoryIds.push(category.id)
                }
            }
            this.createPostForm.get('categories')?.setValue(categoryIds);
        }
    }

    private getCategories(communityId: number): void {
        this.createPostForm.get('categories')?.reset(null);
        this.createPostForm.get('categories')?.disable();
        if (!communityId) return;
        this.categories = [];
        this.createPostForm.get('categories')?.enable();
        this.communitiesService.getCategories(communityId).pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(res => {
                this.categories = res;
                if (!this.categories.length) {
                    this.createPostForm.get('categories')?.disable();
                }
            });
        this.createPostForm.get('categories')?.setValue(0)
    }

    private changeCommunity(communityId: number): void {
        if (!communityId) return;
        const community: Community = this.communities.find(community => community.id === communityId)!
        this.emojiEnabled = community?.emoji_enabled;
        this.contentService.setCurrentDashboardCommunity(community);
    }

    private manageLockState(): void {
        const lock$: Observable<any> = this.lockPostControl.valueChanges;
        lock$.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((res): void => {
                if (res) {
                    this.dateControl.enable();
                    this.timeControl.enable();
                    this.createPostForm.get('lock_post_at')?.enable();
                    this.createPostForm.get('off_comments_at_time')?.enable();
                    this.createPostForm.get('off_likes_at_time')?.enable();
                    this.createPostForm.get('off_dislikes_at_time')?.enable();
                    this.createPostForm.get('lock_post_at')?.addValidators(Validators.required);
                    this.createPostForm.get('lock_post_at')!.updateValueAndValidity();
                } else {
                    this.dateControl.disable();
                    this.timeControl.disable();
                    this.createPostForm.get('lock_post_at')?.reset();
                    this.createPostForm.get('lock_post_at')?.disable();
                    this.createPostForm.get('off_comments_at_time')?.reset();
                    this.createPostForm.get('off_comments_at_time')?.disable();
                    this.createPostForm.get('off_likes_at_time')?.reset();
                    this.createPostForm.get('off_likes_at_time')?.disable();
                    this.createPostForm.get('off_dislikes_at_time')?.reset();
                    this.createPostForm.get('off_dislikes_at_time')?.disable();
                    this.createPostForm.get('lock_post_at')?.removeValidators(Validators.required);
                    this.createPostForm.get('lock_post_at')!.updateValueAndValidity();
                }
            });

        const time$: Observable<any> = this.timeControl.valueChanges;
        const date$: Observable<any> = this.dateControl.valueChanges;

        combineLatest([date$, time$, lock$])
            .pipe(
                distinctUntilChanged(this.customComparator),
                filter(res => !!res[2]),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(res => {
                const date: Date = mergeDateAndTime(res[0], res[1]);
                this.createPostForm.get('lock_post_at')?.patchValue(date);
                this.createPostForm.get('lock_post_at')?.updateValueAndValidity();
            })
    }

    private customComparator(
        prev: [Date, string, boolean],
        curr: [Date, string, boolean]
    ): boolean {
        return prev[0] === curr[0] && prev[1] === curr[1] && prev[2] === curr[2];
    }

    private _filterSubgroups(value: string): string[] {
        let filterValue: string = '';
        if (typeof value == 'string') {
            filterValue = value.toLowerCase();
        }
        let results: any[] = this.subgroups.filter((option: any) => option.name.toLowerCase().includes(filterValue));
        results = results.filter((item: any, index: any): boolean => {
            return (results.indexOf(item) == index);
        });
        return results;
    }


    exitCheck(): void {
        const dialogRef: MatDialogRef<ConfirmationDialogComponent> = this.dialog.open<ConfirmationDialogComponent, ConfirmationDialogData>(ConfirmationDialogComponent, {
            data: {
                showSubmitBtn: true,
                showCancelBtn: true,
                message: 'Are you sure you want to exit? Changes that you have made will not be saved.',
                title: 'Exit',
                showHeader: true,
            }
        });
        dialogRef.afterClosed().subscribe(r => {
            if (r) {
                this.matDialog.close()
            }
        });
    }
}
